// Workflow execution engine — walks a workflow definition, running each step's Action and feeding
// outputs forward. Spec: info-data/Workflows.md.
//
// Because Actions run in the browser (atlas.action.run), the engine lives client-side. It is pure
// given a `runAction(name, input) -> Promise<output>` function, so it unit-tests without a DOM.
//
// Config resolution (spec §3.2): each step's config has `static` (passed verbatim) and `dynamic`
// (values are JSONPath-ish refs like "$.<stepName>.results[0].image" into prior step outputs).
// Conditional next-step (object form): run its action, look up its output in `rules`; fall back to
// `default` if present (our resolution of the spec's open "default next step" question), else end.

import type { ApiRequester } from "./api";
import type {
  ActionApi,
  ConditionalNextStep,
  RunOptions,
  WorkflowApi,
  WorkflowDefinition,
  WorkflowRunResult,
  WorkflowStep,
  WorkflowStepConfig,
} from "./types";

export const MAX_STEPS = 100; // safety bound against cycles

type Outputs = Record<string, unknown>;

// Resolve a "$.step.path.to[0].value" reference against the map of prior outputs.
export function resolveRef(ref: unknown, outputs: Outputs): unknown {
  if (typeof ref !== "string" || !ref.startsWith("$.")) return ref; // literal, not a ref
  const tokens = ref
    .slice(2)
    .replace(/\[(\d+)\]/g, ".$1") // a[0] -> a.0
    .split(".")
    .filter(Boolean);
  let cur: unknown = outputs;
  for (const tok of tokens) {
    if (cur == null) return undefined;
    cur = (cur as Record<string, unknown>)[tok];
  }
  return cur;
}

// Build a step's input by merging static config with resolved dynamic refs.
export function resolveConfig(
  config: WorkflowStepConfig | null | undefined,
  outputs: Outputs,
): Record<string, unknown> {
  if (!config) return {};
  const out: Record<string, unknown> = { ...(config.static || {}) };
  for (const [key, ref] of Object.entries(config.dynamic || {})) {
    out[key] = resolveRef(ref, outputs);
  }
  return out;
}

// Decide the next step id after `step`. Handles string, null, and conditional-object forms.
async function nextStepId(
  step: WorkflowStep,
  outputs: Outputs,
  runAction: (name: string, input: unknown) => Promise<unknown>,
): Promise<string | null> {
  const next = step.nextStepId;
  if (next == null || typeof next === "string") return next ?? null;
  // conditional: run the decider action and branch on its output
  const input = resolveConfig(parseMaybeJson(next.config), outputs);
  const decision = (await runAction(next.action, input)) as string;
  if (Object.prototype.hasOwnProperty.call(next.rules || {}, decision)) {
    return (next.rules as Record<string, string | null>)[decision];
  }
  return next.default ?? null; // our default-next-step resolution
}

function parseMaybeJson(config: ConditionalNextStep["config"]): WorkflowStepConfig | null {
  // The conditional next-step `config` may be a JSON string (per the spec example) or an object.
  if (typeof config === "string") {
    try {
      return JSON.parse(config);
    } catch {
      return {};
    }
  }
  return config ?? null;
}

// Run a whole workflow definition. Returns { outputs, visited } keyed by step *name* (falling back
// to id). `runAction` is injected — in the SDK it is atlas.action.run.
export async function runWorkflow(
  definition: WorkflowDefinition,
  runAction: (name: string, input: unknown) => Promise<unknown>,
  { onStep }: RunOptions = {},
): Promise<WorkflowRunResult> {
  const steps = definition.steps || {};
  const outputs: Outputs = {};
  const visited: string[] = [];
  let currentId: string | null = definition.firstStepId;
  let count = 0;

  while (currentId) {
    if (count++ >= MAX_STEPS) throw new Error("workflow exceeded MAX_STEPS (possible cycle)");
    const step = steps[currentId];
    if (!step) throw new Error(`workflow references unknown step ${currentId}`);

    const input = resolveConfig(step.config, outputs);
    if (onStep) onStep({ id: currentId, name: step.name, action: step.action, input });
    const output = await runAction(step.action, input);

    // Key outputs by step name so dynamic refs ("$.birdland.results...") read naturally; the id is
    // always available as a fallback key.
    outputs[step.name || currentId] = output;
    outputs[currentId] = output;
    visited.push(currentId);

    currentId = await nextStepId(step, outputs, runAction);
  }
  return { outputs, visited };
}

// Factory for window.atlas.workflow.* — binds the engine to the action runner + workflows API.
export function makeWorkflowApi(api: ApiRequester, actionApi: ActionApi): WorkflowApi {
  return {
    // Run a stored workflow by id (fetches its definition, then executes).
    async run(workflowId: string, { onStep }: RunOptions = {}) {
      const wf = await api.request("GET", `/workflows/${encodeURIComponent(workflowId)}`);
      return runWorkflow(wf.definition, (name, input) => actionApi.run(name, input), { onStep });
    },
    // Run an already-loaded definition (e.g. live preview in the editor).
    runDefinition(definition: WorkflowDefinition, opts?: RunOptions) {
      return runWorkflow(definition, (name, input) => actionApi.run(name, input), opts);
    },
    list: () => api.request("GET", "/workflows"),
    listShared: () => api.request("GET", "/workflows/shared"),
    get: (id: string) => api.request("GET", `/workflows/${encodeURIComponent(id)}`),
    create: (name: string, definition: unknown, visibility?: string) =>
      api.request("POST", "/workflows", { body: { name, definition, visibility } }),
    remove: (id: string) => api.request("DELETE", `/workflows/${encodeURIComponent(id)}`),
  };
}
